import { Link } from 'react-router-dom'
import { useMarketingAnalytics, useMarketingAccess } from '../../hooks/useMarketing'
import { useProducts } from '../../hooks/useApi'
import { Card, CardContent } from '../../components/ui/Card'
import { Crown, BarChart3, Download, Share2, Image, Megaphone, PenLine } from 'lucide-react'
import { normalizePlanId } from '../../lib/plans'

export default function MarketingAnalytics() {
  const { data: access } = useMarketingAccess()
  const plan = normalizePlanId(access?.plan)
  const isPremium = plan === 'premium'
  const { data, isLoading } = useMarketingAnalytics(isPremium)
  const { data: products } = useProducts()

  if (!isPremium) {
    return (
      <div className="text-center py-16">
        <Crown className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold">Analytics Marketing — Premium</h2>
        <p className="text-gray-600 mt-2 mb-4">Pantau performa materi promosi Anda</p>
        <Link to="/subscription" className="text-indigo-600 font-medium hover:underline">Upgrade ke Premium →</Link>
      </div>
    )
  }

  if (isLoading) return <div className="text-center py-12 text-gray-500">Memuat analytics...</div>

  const productMap = new Map(products?.map((p: { id: number; name: string }) => [p.id, p.name]) ?? [])

  const stats = [
    { label: 'Banner', value: data?.bannerCount ?? 0, icon: <Image className="w-5 h-5" />, color: 'bg-blue-600' },
    { label: 'Campaign', value: data?.campaignCount ?? 0, icon: <Megaphone className="w-5 h-5" />, color: 'bg-purple-600' },
    { label: 'Caption', value: data?.captionCount ?? 0, icon: <PenLine className="w-5 h-5" />, color: 'bg-pink-600' },
    { label: 'Download', value: data?.downloadCount ?? 0, icon: <Download className="w-5 h-5" />, color: 'bg-emerald-600' },
    { label: 'Share', value: data?.shareCount ?? 0, icon: <Share2 className="w-5 h-5" />, color: 'bg-orange-600' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-indigo-600" /> Analytics Marketing
        </h1>
        <p className="text-gray-600 mt-1">Statistik dan riwayat aktivitas promosi</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`${s.color} rounded-xl p-4 text-white`}>
            <div className="flex justify-between">
              <div>
                <p className="text-white/80 text-sm">{s.label}</p>
                <p className="text-2xl font-bold mt-1">{s.value}</p>
              </div>
              <div className="bg-white/20 p-2 rounded-lg">{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Produk Paling Sering Dipromosikan</h3>
            {data?.productPromoCounts?.length ? (
              <ul className="space-y-2">
                {data.productPromoCounts.map((p: { productId: number; count: number }) => (
                  <li key={p.productId} className="flex justify-between text-sm">
                    <span>{productMap.get(p.productId) || `Produk #${p.productId}`}</span>
                    <span className="font-semibold text-indigo-600">{p.count}x</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Belum ada data</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Jenis Template Favorit</h3>
            {data?.templateFavorites?.length ? (
              <ul className="space-y-2">
                {data.templateFavorites.map((t: { templateId: string; count: number }) => (
                  <li key={t.templateId} className="flex justify-between text-sm">
                    <span className="truncate">{t.templateId}</span>
                    <span className="font-semibold text-indigo-600">{t.count}x</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Belum ada data</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-4">Riwayat Aktivitas</h3>
          {data?.recentActivities?.length ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.recentActivities.map((a: { id: number; action: string; materialType: string | null; createdAt: string }) => (
                <div key={a.id} className="flex justify-between text-sm py-2 border-b border-gray-100">
                  <span>
                    <span className="font-medium capitalize">{a.action.replace(/_/g, ' ')}</span>
                    {a.materialType && <span className="text-gray-500"> · {a.materialType}</span>}
                  </span>
                  <span className="text-gray-400">{new Date(a.createdAt).toLocaleString('id-ID')}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Belum ada aktivitas</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
